/**
 * ShareProjectModal Component
 * Modal for sharing a project with another user by email.
 * Shows email input, role picker, and list of current shares.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Send, Trash2, Loader2, Check, Mail, UserCheck, UserX } from 'lucide-react';
import { Button, Badge } from '@/components/ui';
import { useProjectShares } from '@/hooks/useProjectShares';
import type { ProjectConfig, Role } from '@/lib/types';

interface ShareProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: ProjectConfig;
}

export const ShareProjectModal: React.FC<ShareProjectModalProps> = ({
  isOpen,
  onClose,
  project,
}) => {
  const { t } = useTranslation();
  const { shares, isLoading, shareProject, revokeShare, updateRole } = useProjectShares(
    isOpen ? project.id : null
  );

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('viewer');
  const [isSharing, setIsSharing] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [revokingEmail, setRevokingEmail] = useState<string | null>(null);

  const handleShare = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;

    setIsSharing(true);
    setFeedback(null);
    try {
      const result = await shareProject(trimmed, role);
      setFeedback({
        type: 'success',
        message: result.isExistingUser
          ? t('projectConfig.invitationSentExisting', { email: trimmed })
          : t('projectConfig.invitationSentNew', { email: trimmed }),
      });
      setEmail('');
    } catch (err) {
      setFeedback({
        type: 'error',
        message: err instanceof Error ? err.message : t('projectConfig.failedToShare'),
      });
    } finally {
      setIsSharing(false);
    }
  };

  const handleRevoke = async (recipientEmail: string) => {
    setRevokingEmail(recipientEmail);
    try {
      await revokeShare(recipientEmail);
      setFeedback({ type: 'success', message: t('projectConfig.accessRevoked', { email: recipientEmail }) });
    } catch (err) {
      setFeedback({
        type: 'error',
        message: err instanceof Error ? err.message : t('projectConfig.failedToRevoke'),
      });
    } finally {
      setRevokingEmail(null);
    }
  };

  const handleRoleChange = async (recipientEmail: string, newRole: Role) => {
    try {
      await updateRole(recipientEmail, newRole);
    } catch (err) {
      setFeedback({
        type: 'error',
        message: err instanceof Error ? err.message : t('projectConfig.failedToUpdateRole'),
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">{t('projectConfig.shareProject')}</h2>
            <p className="text-sm text-slate-500 mt-0.5">{project.projectName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Feedback */}
          {feedback && (
            <div
              className={`p-3 rounded-lg text-sm flex items-center gap-2 ${
                feedback.type === 'success'
                  ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                  : 'bg-red-50 border border-red-200 text-red-700'
              }`}
            >
              {feedback.type === 'success' ? (
                <Check className="w-4 h-4 flex-shrink-0" />
              ) : (
                <X className="w-4 h-4 flex-shrink-0" />
              )}
              {feedback.message}
            </div>
          )}

          {/* Share form */}
          <form onSubmit={handleShare} className="space-y-3">
            <label className="block text-sm font-medium text-slate-700">
              {t('projectConfig.inviteByEmail')}
            </label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('projectConfig.emailPlaceholder')}
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg
                           focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                  required
                />
              </div>
              <Button
                type="submit"
                variant="gradient"
                size="md"
                disabled={isSharing || !email.trim()}
              >
                {isSharing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>

            {/* Role picker */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">{t('projectConfig.role')}</span>
              <button
                type="button"
                onClick={() => setRole('viewer')}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  role === 'viewer'
                    ? 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                    : 'bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200'
                }`}
              >
                {t('projectConfig.viewer')}
              </button>
              <button
                type="button"
                onClick={() => setRole('editor')}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  role === 'editor'
                    ? 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                    : 'bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200'
                }`}
              >
                {t('projectConfig.editor')}
              </button>
            </div>
          </form>

          {/* Current shares */}
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-3">
              {t('projectConfig.sharedWith', { count: shares.length })}
            </h3>

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : shares.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">
                {t('projectConfig.notSharedYet')}
              </p>
            ) : (
              <div className="space-y-2">
                {shares.map((share) => (
                  <div
                    key={share.recipientEmail}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="flex-shrink-0">
                        {share.isExistingUser ? (
                          <UserCheck className="w-4 h-4 text-emerald-500" />
                        ) : (
                          <UserX className="w-4 h-4 text-amber-500" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {share.recipientEmail}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge
                            variant={share.status === 'accepted' ? 'success' : 'warning'}
                            size="sm"
                          >
                            {share.status}
                          </Badge>
                          <select
                            value={share.role}
                            onChange={(e) =>
                              handleRoleChange(share.recipientEmail, e.target.value as Role)
                            }
                            className="text-xs bg-transparent border-none text-slate-500 cursor-pointer p-0"
                          >
                            <option value="viewer">{t('projectConfig.viewer')}</option>
                            <option value="editor">{t('projectConfig.editor')}</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRevoke(share.recipientEmail)}
                      disabled={revokingEmail === share.recipientEmail}
                      className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                      title="Revoke access"
                    >
                      {revokingEmail === share.recipientEmail ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t border-slate-200 bg-slate-50">
          <Button variant="outline" size="md" onClick={onClose}>
            {t('projectConfig.done')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ShareProjectModal;
